import * as React from 'react';
import { useState, useCallback, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import Webcam, { type WebcamProps } from "react-webcam";
import { isFunction } from "../helpers/utils";
import boopSfx from './shutter.ogg';

type Props = Readonly<WebcamProps & {
	onStatusChange?: (status: WebcamCaptureState) => void,
	onImageCapture?: (image?: string) => void,
}>

export enum WebcamCaptureState {
	Blank = 'blank',
	NoWebcamAccess = 'noAccess',
	Image = 'image',
}

const shutterAudio = new Audio(boopSfx);
// shutterAudio.volume = 0.9;

function WebcamCapture({className = '', onStatusChange, onImageCapture, ...otherProps}: Props) {

	const webcamRef = useRef<Webcam>(null);
	const [status, setStatus] = useState<WebcamCaptureState>(WebcamCaptureState.Blank);
	const [image, setImage] = useState<string>(null);

	const capture = useCallback(() => {

		const imageSrc = webcamRef.current?.getScreenshot();
		// console.log(imageSrc);
		if (imageSrc !== null) {
			shutterAudio.play();
			setStatus(WebcamCaptureState.Image);
			setImage(imageSrc);
		}
	}, [webcamRef]);

	const supportedConstraints = navigator.mediaDevices.getSupportedConstraints();
	let videoConstraints= {};
	if (supportedConstraints?.facingMode) {
		videoConstraints = { ...videoConstraints, facingMode: "user" };
	}

	useEffect(() => {
		if (isFunction(onStatusChange)) {
			onStatusChange(status);
		}
	}, [status]);

	useEffect(() => {
		if (isFunction(onImageCapture)) {
			onImageCapture(image);
		}
	}, [image]);

	return (<>
		<div className={clsx("text-center", className)}>
			<div className={clsx(
				"bg-primary-200 relative w-auto h-auto aspect-[4/3] sm:w-[304px] sm:h-[228px]",
				status === WebcamCaptureState.NoWebcamAccess ? "w-[260px] h-[195px]" : "w-auto h-auto"
			)}>
				{status === WebcamCaptureState.Blank && <Webcam
					audio={false}
					ref={webcamRef}
					onUserMediaError={() => {
						setStatus(WebcamCaptureState.NoWebcamAccess)
					}}
					videoConstraints={videoConstraints}
					{...otherProps}
				/>}

				{status === WebcamCaptureState.Image && <img src={image} alt="" />}

				{status === WebcamCaptureState.NoWebcamAccess && <div className="flex align-middle items-center absolute inset-0">
					<h4 className="w-full text-red-700 text-center my-0">No webcam access</h4>
				</div>}
			</div>
			{status !== WebcamCaptureState.Image &&
				<button
					className="mt-2 mb-0 inline-block"
					disabled={status === WebcamCaptureState.NoWebcamAccess}
					onClick={(event) => {
						event.preventDefault();
						capture();
					}}>
					Capture
				</button>
			}
			{status === WebcamCaptureState.Image &&
				<button
					className="mt-2 mb-0 inline-block"
					onClick={(event) => {
						event.preventDefault();
						setImage(null);
						setStatus(WebcamCaptureState.Blank);
					}}>
					Recapture
				</button>
			}
		</div>

		{/*<Dump value={status}/>*/}
	</>);
}

export default WebcamCapture;
